import { useState, useContext, forwardRef, useImperativeHandle } from "react";
import { Card, InputNumber, Tag, Space, Typography, Button, message } from "antd";
import { setSpecSizeToSupabase, recomputeHarvestPlan } from "./harvestOptimiser.service";
import { HarvestContext } from "./HarvestContext";
import { cmToInches, inchesToCm } from "../../../utils/cmToInchesConversion";

const { Text } = Typography;
const id = crypto.randomUUID();

const SpecSizeComponent = forwardRef(({ onSaveComplete, onSave, showFootnote = false, hideButton = false, onSpecSizeChange }, ref) => {
  const context = useContext(HarvestContext);
  const siteConfig = context?.siteConfig ?? null;
  const isImperial = context?.currentSite?.metadata?.siteConfig?.display_units === "imperial";
  const unit = isImperial ? "in" : "cm";

  const [loading, setLoading] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [minSpecOverride, setMinimumSpecSize] = useState(null);
  const [maxSpecOverride, setMaximumSpecSize] = useState(null);

  const minimumSpecSize = minSpecOverride ?? siteConfig?.minimum_spec_size;
  const maximumSpecSize = maxSpecOverride ?? siteConfig?.maximum_spec_size;

  const isUpdated = minSpecOverride !== null || maxSpecOverride !== null;

  const handleMinChange = (value) => {
    const cmValue = isImperial ? inchesToCm(value) : value;
    setMinimumSpecSize(cmValue);
    setShowSave(true);
    onSpecSizeChange?.();
  };

  const handleMaxChange = (value) => {
    const cmValue = isImperial ? inchesToCm(value) : value;
    setMaximumSpecSize(cmValue);
    setShowSave(true);
    onSpecSizeChange?.();
  };

  const handleSpecSizeSave = async () => {
    setLoading(true);
    try {
      const constraints = {
        minimum_spec_size: minimumSpecSize,
        maximum_spec_size: maximumSpecSize,
      };
      await setSpecSizeToSupabase(context.currentSite.name, constraints);
      await recomputeHarvestPlan(context.currentOrg, context.currentSite.id);
      message.success('Configuration updated successfully');
      setShowSave(false);
      if (onSave) {
        onSave();
      }
      onSaveComplete?.();
    } catch (error) {
      message.error('Failed to save spec size');
      console.error(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    save: handleSpecSizeSave,
  }));

  let constraints = {
    minimum_spec_size: minimumSpecSize,
    maximum_spec_size: maximumSpecSize,
  };

  const isValidState = typeof minimumSpecSize === 'number' && typeof maximumSpecSize === 'number';

  return (
    <>
      <Card title="Spec Size (Head Diameter)" size="small">
        <Space vertical={true}>
          <Text strong>
            Grade 1 &nbsp;&nbsp;
            <Tag color="gold">Premium</Tag>
          </Text>

          {id && (
            <>
              <Space>
                <InputNumber
                  id={`min-spec-${id}`}
                  value={minimumSpecSize != null && isImperial ? cmToInches(minimumSpecSize) : minimumSpecSize}
                  precision={isImperial ? 2 : undefined}
                  step={isImperial ? 0.1 : 1}
                  onChange={(value) => {
                    if (value !== null) {
                      handleMinChange(value);
                    }
                  }}
                  min={1}
                />
                <span>to</span>
                <InputNumber
                  id={`max-spec-${id}`}
                  value={maximumSpecSize != null && isImperial ? cmToInches(maximumSpecSize) : maximumSpecSize}
                  precision={isImperial ? 2 : undefined}
                  step={isImperial ? 0.1 : 1}
                  onChange={(value) => {
                    if (value !== null) {
                      handleMaxChange(value);
                    }
                  }}
                  max={isImperial ? Number(cmToInches(50).toFixed(2)) : 50}
                />
                <span>{unit}</span>
                {showSave && !hideButton && (
                  <Button
                    type="primary"
                    onClick={async () => {
                      setLoading(true);
                      try {
                        await setSpecSizeToSupabase(context.currentSite.name, constraints);
                        await recomputeHarvestPlan(context.currentOrg, context.currentSite.id);
                        message.success('Configuration updated successfully');
                        setShowSave(false);
                        onSaveComplete?.();
                      } catch (error) {
                        message.error('Failed to save spec size');
                        console.error(error);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={!isValidState}
                    loading={loading}
                  >
                    Save
                  </Button>
                )}
              </Space>
              {showFootnote && (
                <Text type="danger" style={{ fontSize: '12px' }}>
                  Changes in spec size will take 3 to 6 minutes to take effect.<br />
                  Please reload the page to see the forecast for new spec sizes
                </Text>
              )}
            </>
          )}
        </Space>
      </Card>
    </>
  );
});

SpecSizeComponent.displayName = 'SpecSize';
export const SpecSize = SpecSizeComponent;
